import { useState } from 'react';
import { useList, useDelete, useInvalidate } from '@refinedev/core';
import { Plus, Edit, Eye, Trash2, Link2, MousePointerClick, UserPlus, LogIn, TrendingUp, Route } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable, TableToolbar, type BulkAction, type Column, type SortState } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { notify } from '@/lib/notify';
import { UtmSourceStatsDialog } from './UtmSourceStatsDialog';
import { UtmRedirectsDialog } from './UtmRedirectsDialog';
import { UtmCopyLinkDropdown } from './UtmCopyLinkDropdown';
import { UtmSourcesFormFields } from './form';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import type { UtmSourceItem, UtmSourceStatsType } from './types';

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function UtmSourcesList() {
  useScrollRestoration();
  const [page, setPage] = usePersistedState('utm-sources:page', 1);
  const [search, setSearch] = usePersistedState('utm-sources:search', '');
  const [sort, setSort] = usePersistedState<SortState | null>('utm-sources:sort', null);
  const [deleteTargets, setDeleteTargets] = useState<UtmSourceItem[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editSource, setEditSource] = useState<UtmSourceItem | null>(null);
  const [statsDialog, setStatsDialog] = useState<{ source: UtmSourceItem; type: UtmSourceStatsType } | null>(null);
  const [redirectsDialogSource, setRedirectsDialogSource] = useState<UtmSourceItem | null>(null);
  const [tableVersion, setTableVersion] = useState(0);
  const pageSize = 20;

  const invalidate = useInvalidate();

  const listResult = useList<UtmSourceItem>({
    resource: 'utm-sources',
    pagination: { mode: 'server', currentPage: page, pageSize },
    filters: search.trim()
      ? [{ field: 'search', operator: 'contains', value: search.trim() }]
      : [],
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const isLoading = listResult.query.isLoading;
  const data = listResult.result.data;
  const total = listResult.result.total ?? 0;

  const deleteMutation = useDelete();

  const handleDelete = async () => {
    if (deleteTargets.length === 0) { return; }

    try {
      for (const source of deleteTargets) {
        await deleteMutation.mutateAsync({ resource: 'utm-sources', id: source.id });
      }
      notify.success(deleteTargets.length === 1 ? 'UTM источник удалён' : `Удалено UTM источников: ${deleteTargets.length}`);
      setDeleteTargets([]);
      setTableVersion((version) => version + 1);
      invalidate({ resource: 'utm-sources', invalidates: ['list'] });
    } catch {
      notify.error('Ошибка удаления');
    }
  };


  const columns: Column<UtmSourceItem>[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      width: '60px',
      render: (value) => (
        <span className="text-sm text-muted-foreground">#{String(value)}</span>
      ),
    },
    {
      key: 'displayName',
      header: 'Отображаемое название',
      sortable: true,
      render: (_, source) => (
        <span className="font-medium">{source.displayName}</span>
      ),
    },
    {
      key: 'name',
      header: 'Код (name)',
      sortable: true,
      width: '160px',
      render: (value) => (
        <code className="text-sm bg-muted px-2 py-1 rounded">{String(value)}</code>
      ),
    },
    {
      key: 'shortCode',
      header: 'Ссылка',
      width: '280px',
      render: (_, source) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {source.botLink ? (
            <code className="max-w-[200px] truncate rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              {source.botLink}
            </code>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
          <UtmCopyLinkDropdown source={source} />
        </div>
      ),
    },
    {
      key: 'stats',
      header: 'Статистика',
      width: '260px',
      render: (_, source) => (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <button
            type="button"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
            onClick={(event) => {
              event.stopPropagation();
              setStatsDialog({ source, type: 'visits' });
            }}
          >
            <MousePointerClick className="h-3.5 w-3.5" />
            {source.stats.visitsCount}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
            onClick={(event) => {
              event.stopPropagation();
              setStatsDialog({ source, type: 'registered' });
            }}
          >
            <UserPlus className="h-3.5 w-3.5" />
            {source.stats.registeredCount}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
            onClick={(event) => {
              event.stopPropagation();
              setStatsDialog({ source, type: 'authorized' });
            }}
          >
            <LogIn className="h-3.5 w-3.5" />
            {source.stats.authorizedCount}
          </button>
          <span className="flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5" />
            {source.stats.conversionRate.toFixed(1)}%
          </span>
        </div>
      ),
    },
    {
      key: 'pendingRedirectCount',
      header: 'Не перешли',
      width: '110px',
      render: (_, source) => {
        if (!source.redirectUrl) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        return (
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            title={`Цель: ${source.redirectUrl}. Открыли ссылку, но ещё не перешли.`}
            onClick={(event) => {
              event.stopPropagation();
              setRedirectsDialogSource(source);
            }}
          >
            <Route className="h-3.5 w-3.5" />
            {source.pendingRedirectCount ?? 0}
          </button>
        );
      },
    },
    {
      key: 'autoTag',
      header: 'Авто-тег',
      width: '160px',
      render: (_, source) => {
        if (!source.autoTag) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        return (
          <Badge
            variant="outline"
            style={{ borderColor: source.autoTag.color, color: source.autoTag.color }}
          >
            {source.autoTag.name}
          </Badge>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Создан',
      sortable: true,
      width: '120px',
      render: (value) => (
        <span className="text-sm text-muted-foreground">{formatDate(String(value))}</span>
      ),
    },
  ];

  const bulkActions: BulkAction<UtmSourceItem>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      showOnlyWhenSelected: true,
      onClick: (sources) => {
        if (sources.length !== 1) {
          notify.info('Для просмотра выберите один источник');
          return;
        }
        setEditSource(sources[0]);
      },
    },
    {
      label: 'Редактировать',
      icon: <Edit className="h-4 w-4" />,
      showOnlyWhenSelected: true,
      onClick: (sources) => {
        if (sources.length !== 1) {
          notify.info('Для редактирования выберите один источник');
          return;
        }
        setEditSource(sources[0]);
      },
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      showOnlyWhenSelected: true,
      onClick: setDeleteTargets,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="UTM источники"
        description="Управление UTM источниками для отслеживания трафика"
        sticky
        icon={<Link2 className="h-5 w-5" />}
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Создать источник
          </Button>
        }
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(value) => { setSearch(value); setPage(1); }}
        searchPlaceholder="Поиск по названию или коду..."
      />

      <DataTable
        key={tableVersion}
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={setEditSource}
        emptyMessage="Нет UTM источников"
        rowKey="id"
        selectable
        sort={sort ?? undefined}
        onSortChange={(newSort) => { setSort(newSort ?? null); setPage(1); }}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteTargets.length > 0} onOpenChange={() => setDeleteTargets([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTargets.length === 1 ? 'Удалить UTM источник?' : `Удалить UTM источников: ${deleteTargets.length}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Выбранные UTM источники будут удалены навсегда.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { void handleDelete(); }}
              disabled={deleteMutation.mutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.mutation.isPending ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UtmSourceStatsDialog
        source={statsDialog?.source ?? null}
        type={statsDialog?.type ?? null}
        open={statsDialog !== null}
        onOpenChange={(open) => {
          if (!open) { setStatsDialog(null); }
        }}
      />

      <UtmRedirectsDialog
        source={redirectsDialogSource}
        open={redirectsDialogSource !== null}
        onOpenChange={(open) => {
          if (!open) { setRedirectsDialogSource(null); }
        }}
      />

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Создать UTM источник</DialogTitle>
            <DialogDescription>
              Источник создаст Telegram-ссылку и сможет автоматически назначать тег пользователям.
            </DialogDescription>
          </DialogHeader>
          <UtmSourcesFormFields
            key={isCreateOpen ? 'create-open' : 'create-closed'}
            isEdit={false}
            initialState={{ displayName: '', name: '', autoTagId: null, redirectUrl: null }}
            onBack={() => setIsCreateOpen(false)}
            layout="dialog"
            onSuccess={() => {
              setIsCreateOpen(false);
              invalidate({ resource: 'utm-sources', invalidates: ['list'] });
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editSource !== null} onOpenChange={(open) => {
        if (!open) { setEditSource(null); }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Редактировать UTM источник</DialogTitle>
            <DialogDescription>
              Обновите отображаемое название и авто-тег источника.
            </DialogDescription>
          </DialogHeader>
          {editSource && (
            <UtmSourcesFormFields
              key={`edit-${editSource.id}`}
              isEdit
              id={String(editSource.id)}
              initialState={{
                displayName: editSource.displayName,
                name: editSource.name,
                autoTagId: editSource.autoTagId,
                redirectUrl: editSource.redirectUrl ?? null,
              }}
              shortCode={editSource.shortCode}
              botLink={editSource.botLink}
              onBack={() => setEditSource(null)}
              layout="dialog"
              onSuccess={() => {
                setEditSource(null);
                setTableVersion((version) => version + 1);
                invalidate({ resource: 'utm-sources', invalidates: ['list'] });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
